
///
/// EarthBrowser Design System - Colors
///
/// Centralized color definitions following the science-themed design language
/// Reference: documentation/DESIGN_SYSTEM.md
///

import { rgba } from "@stovokor/math/color"

export class DesignColors {
	// Typography colors
	static readonly TITLE = rgba(1, 1, 1, 1)              // Pure white for titles
	static readonly VALUE = rgba(0.95, 0.95, 0.95, 1)     // Near white for values
	static readonly LABEL_MUTED = rgba(0.65, 0.65, 0.65, 1) // Muted gray for labels

	// Theme-specific label colors
	static readonly LABEL_BLUE = rgba(0.7, 0.85, 1, 0.9)      // Light blue (satellites, locations)
	static readonly LABEL_AMBER = rgba(1, 0.85, 0.5, 0.9)    // Amber (aircraft, warnings)
	static readonly LABEL_TEAL = rgba(0.5, 0.9, 0.85, 0.9)   // Teal (ships, ocean)
	static readonly LABEL_PURPLE = rgba(0.8, 0.65, 1, 0.9)   // Purple (ground stations)
	static readonly LABEL_VOLCANO = rgba(0.85, 0.65, 0.55, 1) // Orange-tinted for volcanoes
	static readonly LABEL_QUAKE = rgba(1, 0.55, 0.55, 0.9)   // Red-tinted for earthquakes
	static readonly LABEL_BUOY = rgba(0.4, 0.8, 1, 0.9)      // Ocean blue for buoys

	// Glassmorphism elements
	static readonly BORDER_GLASS = rgba(1, 1, 1, 0.2)      // Standard glass border
	static readonly BORDER_GLASS_STRONG = rgba(1, 1, 1, 0.3) // Stronger glass border

	// Connector lines
	static readonly CONNECTOR = rgba(1, 1, 1, 0.5)         // White connector lines
	static readonly CONNECTOR_GLOW = rgba(1, 1, 1, 0.4)   // Connector glow effect
	static readonly CONNECTOR_VOLCANO = rgba(0.92, 0.4, 0.2, 0.4) // Orange connector for volcanoes
	static readonly CONNECTOR_QUAKE = rgba(1, 0.3, 0.3, 0.4) // Red connector for earthquakes
	static readonly CONNECTOR_BUOY = rgba(0.2, 0.6, 1, 0.4)  // Ocean blue connector for buoys

	// 3D ember dome marker colors
	// Earthquakes use a single deep red (red-800 #991b1b) so they can never
	// be confused with volcano orange. Magnitude is communicated by size and
	// animation, not color.
	static readonly MARKER_QUAKE = rgba(0.6, 0.106, 0.106, 0.8)    // red-800
	// Volcanoes use the design system orange (orange-500 #f97316)
	static readonly MARKER_VOLCANO = rgba(0.976, 0.451, 0.09, 0.85) // orange-500
	// Wildfires use a vivid crimson (rose-600 #e11d48): clearly redder than
	// volcano orange-500 and much brighter than quake red-800, so the three
	// hazard markers stay distinguishable. Activity is communicated by
	// pulse, not hue alone.
	static readonly MARKER_WILDFIRE = rgba(0.882, 0.114, 0.282, 0.85)
	// Perimeter polygon styling: translucent fill + solid outline. Contained
	// fires desaturate and dim rather than changing hue, and containment is
	// always shown as text in the UI so color is never the only channel.
	static readonly FIRE_FILL_ACTIVE = rgba(0.92, 0.14, 0.3, 0.28)
	static readonly FIRE_OUTLINE_ACTIVE = rgba(0.957, 0.247, 0.369, 0.9)  // rose-500
	static readonly FIRE_FILL_CONTAINED = rgba(0.55, 0.35, 0.38, 0.14)
	static readonly FIRE_OUTLINE_CONTAINED = rgba(0.65, 0.45, 0.48, 0.7)
	static readonly FIRE_FILL_FOCUSED = rgba(1.0, 0.25, 0.4, 0.42)
	static readonly FIRE_OUTLINE_FOCUSED = rgba(1.0, 0.55, 0.62, 1.0)
	// Weather alert polygons follow the familiar NWS severity convention
	// (yellow advisory → orange watch → red warning → magenta extreme) so
	// the ramp reads instantly to anyone who has seen a weather map.
	// Translucent fill + solid outline like fire perimeters; severity is
	// always shown as text in the UI so color is never the only channel.
	static readonly ALERT_FILL_EXTREME = rgba(0.78, 0.16, 0.82, 0.26)
	static readonly ALERT_OUTLINE_EXTREME = rgba(0.88, 0.35, 0.92, 0.9)
	static readonly ALERT_FILL_SEVERE = rgba(0.92, 0.18, 0.14, 0.22)
	static readonly ALERT_OUTLINE_SEVERE = rgba(0.98, 0.32, 0.26, 0.9)
	static readonly ALERT_FILL_MODERATE = rgba(0.96, 0.56, 0.1, 0.18)
	static readonly ALERT_OUTLINE_MODERATE = rgba(1.0, 0.66, 0.18, 0.85)
	static readonly ALERT_FILL_MINOR = rgba(0.95, 0.85, 0.2, 0.14)
	static readonly ALERT_OUTLINE_MINOR = rgba(1.0, 0.9, 0.32, 0.8)
	// Tropical cyclones use the standard hurricane-tracker intensity ramp
	// (blue depression → cyan storm → yellow/orange/red hurricane) so
	// intensity is readable at a glance and matches every other tracker
	// users know. Track segments are tinted with this same ramp; the cone
	// stays neutral white so it reads as uncertainty, not intensity.
	static readonly STORM_CATEGORY = [
		rgba(0.369, 0.729, 1.0, 0.85),  // TD  #5ebaff
		rgba(1.0, 1.0, 0.8, 0.85),      // Cat 1 #ffffcc
		rgba(1.0, 0.906, 0.459, 0.85),  // Cat 2 #ffe775
		rgba(1.0, 0.757, 0.251, 0.85),  // Cat 3 #ffc140
		rgba(1.0, 0.561, 0.125, 0.85),  // Cat 4 #ff8f20
		rgba(1.0, 0.376, 0.376, 0.85),  // Cat 5 #ff6060
	]
	static readonly STORM_TROPICAL_STORM = rgba(0.0, 0.98, 0.957, 0.85) // TS #00faf4
	// The current-position marker is the classic newscast cyclone symbol in
	// solid hurricane red; intensity stays on the ember dome color ramp
	static readonly MARKER_STORM_SYMBOL = rgba(0.85, 0.11, 0.16, 1.0)
	static readonly STORM_CONE_FILL = rgba(1, 1, 1, 0.09)
	static readonly STORM_CONE_OUTLINE = rgba(1, 1, 1, 0.4)
	static readonly STORM_CONE_FILL_FOCUSED = rgba(1, 1, 1, 0.16)
	static readonly STORM_CONE_OUTLINE_FOCUSED = rgba(1, 1, 1, 0.75)
	static readonly STORM_TRACK_OBSERVED = rgba(1, 1, 1, 0.35)
	static readonly STORM_TRACK_FORECAST = rgba(1, 1, 1, 0.75)

	// Coastal watch/warning segments use the standard NHC palette so anyone
	// who has seen a hurricane graphic reads them instantly: warning colors
	// are saturated, watch colors are their lighter counterparts
	static readonly STORM_WW_HURRICANE_WARNING = rgba(0.88, 0.11, 0.14, 0.95)
	static readonly STORM_WW_HURRICANE_WATCH = rgba(1.0, 0.42, 0.71, 0.95)
	static readonly STORM_WW_TS_WARNING = rgba(0.25, 0.47, 1.0, 0.95)
	static readonly STORM_WW_TS_WATCH = rgba(1.0, 0.84, 0.31, 0.95)

	// Current wind extent rings (34/50/64 kt), translucent so stacked rings
	// build up naturally toward the center
	static readonly STORM_WIND_34_FILL = rgba(1.0, 0.84, 0.31, 0.08)
	static readonly STORM_WIND_34_OUTLINE = rgba(1.0, 0.84, 0.31, 0.45)
	static readonly STORM_WIND_50_FILL = rgba(1.0, 0.55, 0.15, 0.12)
	static readonly STORM_WIND_50_OUTLINE = rgba(1.0, 0.55, 0.15, 0.5)
	static readonly STORM_WIND_64_FILL = rgba(0.95, 0.2, 0.2, 0.16)
	static readonly STORM_WIND_64_OUTLINE = rgba(0.95, 0.2, 0.2, 0.55)

	// Forecast point time/category labels and the landfall callout
	static readonly STORM_LABEL_TEXT = rgba(1, 1, 1, 0.92)
	/** Drop shadow under storm labels so they read over the white cloud shield */
	static readonly STORM_LABEL_SHADOW = rgba(0.05, 0.08, 0.14, 0.55)
	static readonly STORM_LANDFALL = rgba(1.0, 0.35, 0.25, 0.95)

	/** Marker color for a storm's Saffir-Simpson category and wind speed */
	static stormCategoryColor(category: number, windKt: number | null) {
		if (category >= 1) {
			return DesignColors.STORM_CATEGORY[Math.min(category, 5)]
		}
		// Below hurricane strength: tropical storm (≥34 kt) vs depression
		return (windKt ?? 0) >= 34 ? DesignColors.STORM_TROPICAL_STORM : DesignColors.STORM_CATEGORY[0]
	}

	// Satellites are tinted by orbit class: LEO keeps the signature cyan,
	// MEO/HEO a cooler blue, GEO a warm gold (distinct from volcano orange)
	static readonly MARKER_SATELLITE_LEO = rgba(0.2, 0.8, 1.0, 0.85)   // cyan
	static readonly MARKER_SATELLITE_MEO = rgba(0.35, 0.55, 1.0, 0.85) // blue
	static readonly MARKER_SATELLITE_GEO = rgba(1.0, 0.78, 0.25, 0.85) // gold
	// Debris and spent rocket bodies drop the orbit tint for a quiet slate
	// grey so fragment clouds read as dust behind the working satellites
	static readonly MARKER_SATELLITE_DEBRIS = rgba(0.54, 0.58, 0.64, 0.7) // slate-400

	// Orientation indicators (north arrows, compass elements)
	static readonly NORTH_ARROW = rgba(1, 1, 1, 0.9)      // White for visibility on maps

	// 3D Control Gizmo colors (selection spheres, manipulation handles)
	static readonly GIZMO_HORIZONTAL = rgba(0.02, 0.71, 0.83, 0.5)  // cyan-500 at 50% for yaw rotation
	static readonly GIZMO_VERTICAL = rgba(0.08, 0.72, 0.65, 0.5)    // teal-500 at 50% for pitch rotation
	static readonly GIZMO_ZOOM = rgba(0.9, 0.9, 0.95, 0.5)          // Near white for zoom indicators
	static readonly GIZMO_WIREFRAME = rgba(1, 1, 1, 0.4)            // White for wireframe guides

	// Background colors
	static readonly BACKDROP_DARK = rgba(0, 0, 0, 0.4)    // Standard glassmorphism backdrop
	static readonly BACKDROP_DARKER = rgba(0, 0, 0, 0.5)  // Stronger backdrop

	// Modern dark panel
	static readonly PANEL_DARK_BG = rgba(0.12, 0.14, 0.16, 0.95) // Dark panel background
	static readonly PANEL_DARK_BORDER = rgba(0.4, 0.5, 0.6, 0.7) // Dark panel border
	static readonly PANEL_DARK_SHADOW = rgba(0, 0, 0, 0.8)       // Dark panel shadow

	/**
	 * Creates a satellite-themed gradient (blue to cyan)
	 */
	static createSatelliteGradient(context: CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D, width: number, height: number): CanvasGradient {
		const gradient = context.createLinearGradient(0, 0, width, height)
		gradient.addColorStop(0, 'rgba(37, 99, 235, 0.15)')  // blue-600
		gradient.addColorStop(1, 'rgba(6, 182, 212, 0.15)')   // cyan-500
		return gradient
	}

	/**
	 * Creates a location/cities-themed gradient (blue to cyan, slightly stronger)
	 */
	static createLocationGradient(context: CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D, width: number, height: number): CanvasGradient {
		const gradient = context.createLinearGradient(0, 0, width, height)
		gradient.addColorStop(0, 'rgba(37, 99, 235, 0.2)')   // blue-600
		gradient.addColorStop(1, 'rgba(6, 182, 212, 0.2)')    // cyan-500
		return gradient
	}

	/**
	 * Creates a volcano-themed gradient (orange to red)
	 */
	static createVolcanoGradient(context: CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D, width: number, height: number): CanvasGradient {
		const gradient = context.createLinearGradient(0, 0, width, height)
		gradient.addColorStop(0, 'rgba(234, 88, 12, 0.15)')  // orange-600
		gradient.addColorStop(1, 'rgba(220, 38, 38, 0.15)')  // red-600
		return gradient
	}

	/**
	 * Creates an aircraft-themed gradient (amber to orange)
	 */
	static createAircraftGradient(context: CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D, width: number, height: number): CanvasGradient {
		const gradient = context.createLinearGradient(0, 0, width, height)
		gradient.addColorStop(0, 'rgba(245, 158, 11, 0.15)') // amber-500
		gradient.addColorStop(1, 'rgba(249, 115, 22, 0.15)') // orange-500
		return gradient
	}

	/**
	 * Creates a ship/vessel-themed gradient (teal to cyan)
	 */
	static createShipGradient(context: CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D, width: number, height: number): CanvasGradient {
		const gradient = context.createLinearGradient(0, 0, width, height)
		gradient.addColorStop(0, 'rgba(20, 184, 166, 0.15)') // teal-500
		gradient.addColorStop(1, 'rgba(6, 182, 212, 0.15)')  // cyan-500
		return gradient
	}

	/**
	 * Creates a ground station-themed gradient (purple to violet)
	 */
	static createGroundStationGradient(context: CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D, width: number, height: number): CanvasGradient {
		const gradient = context.createLinearGradient(0, 0, width, height)
		gradient.addColorStop(0, 'rgba(168, 85, 247, 0.15)') // purple-500
		gradient.addColorStop(1, 'rgba(139, 92, 246, 0.15)') // violet-500
		return gradient
	}

	/**
	 * Creates an earthquake-themed gradient (red to dark red)
	 */
	static createQuakeGradient(context: CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D, width: number, height: number): CanvasGradient {
		const gradient = context.createLinearGradient(0, 0, width, height)
		gradient.addColorStop(0, 'rgba(239, 68, 68, 0.15)')  // red-500
		gradient.addColorStop(1, 'rgba(185, 28, 28, 0.15)')  // red-700
		return gradient
	}

	/**
	 * Creates a buoy/ocean-themed gradient (blue to cyan)
	 */
	static createBuoyGradient(context: CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D, width: number, height: number): CanvasGradient {
		const gradient = context.createLinearGradient(0, 0, width, height)
		gradient.addColorStop(0, 'rgba(59, 130, 246, 0.15)')  // blue-500
		gradient.addColorStop(1, 'rgba(6, 182, 212, 0.15)')   // cyan-500
		return gradient
	}

	/**
	 * Creates a default/neutral gradient
	 */
	static createDefaultGradient(context: CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D, width: number, height: number): CanvasGradient {
		const gradient = context.createLinearGradient(0, 0, width, height)
		gradient.addColorStop(0, 'rgba(71, 85, 105, 0.15)') // slate-600
		gradient.addColorStop(1, 'rgba(51, 65, 85, 0.15)')  // slate-700
		return gradient
	}
}
